import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface ConsentPreferences {
  essential_cookies: boolean;
  analytics_cookies: boolean;
  marketing_cookies: boolean;
  personalization_cookies: boolean;
  third_party_sharing: boolean;
  jurisdiction: string;
}

const DEFAULT_PREFERENCES: ConsentPreferences = {
  essential_cookies: true,
  analytics_cookies: false,
  marketing_cookies: false,
  personalization_cookies: false,
  third_party_sharing: false,
  jurisdiction: "us",
};

const LOCAL_STORAGE_KEY = "castready_consent_preferences";

export function useConsentPreferences() {
  const { user, profile } = useAuth();
  const [preferences, setPreferences] = useState<ConsentPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasConsented, setHasConsented] = useState(false);

  // Check if user is a minor (requires parent consent)
  const isMinor = profile?.age_group && ["kid", "tween", "teen"].includes(profile.age_group);
  const isCoppaAge = profile?.age_group && ["kid", "tween"].includes(profile.age_group);

  // Fetch preferences from database or localStorage
  const fetchPreferences = useCallback(async () => {
    setIsLoading(true);
    
    try {
      // If logged in, fetch from database
      if (user) {
        const { data, error } = await supabase
          .from("user_consent_preferences")
          .select("user_id, essential_cookies, analytics_cookies, marketing_cookies, personalization_cookies, third_party_sharing, jurisdiction, last_updated")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error && error.code !== "PGRST116") {
          console.error("Error fetching consent preferences:", error);
        }

        if (data) {
          setPreferences({
            essential_cookies: data.essential_cookies,
            analytics_cookies: data.analytics_cookies ?? false,
            marketing_cookies: data.marketing_cookies ?? false,
            personalization_cookies: data.personalization_cookies ?? false,
            third_party_sharing: data.third_party_sharing ?? false,
            jurisdiction: data.jurisdiction ?? "us",
          });
          setHasConsented(true);
        } else {
          // Check localStorage for anonymous preferences
          const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
          if (stored) {
            setPreferences(JSON.parse(stored));
            setHasConsented(true);
          } else {
            setPreferences(null);
            setHasConsented(false);
          }
        }
      } else {
        // Anonymous user - check localStorage
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (stored) {
          setPreferences(JSON.parse(stored));
          setHasConsented(true);
        } else {
          setPreferences(null);
          setHasConsented(false);
        }
      }
    } catch (error) {
      console.error("Error in fetchPreferences:", error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  // Save preferences
  const savePreferences = async (newPreferences: ConsentPreferences) => {
    // Always store in localStorage for anonymous tracking
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newPreferences));
    
    // If logged in, save to database
    if (user) {
      const { error } = await supabase
        .from("user_consent_preferences")
        .upsert({
          user_id: user.id,
          ...newPreferences,
          last_updated: new Date().toISOString(),
          updated_via: "cookie_banner",
        });

      if (error) {
        console.error("Error saving consent preferences:", error);
      }

      // Log the consent event
      await supabase.from("compliance_audit_log").insert([{
        event_type: "consent_given",
        user_id: user.id,
        description: "User updated cookie consent preferences",
        metadata: JSON.parse(JSON.stringify(newPreferences)),
      }]);
    }

    setPreferences(newPreferences);
    setHasConsented(true);
  };

  // Accept all cookies
  const acceptAll = async () => {
    await savePreferences({
      ...DEFAULT_PREFERENCES,
      analytics_cookies: true,
      marketing_cookies: !isCoppaAge, // No marketing for COPPA-age users
      personalization_cookies: true,
      third_party_sharing: !isMinor, // No third-party for minors
    });
  };

  // Reject all optional cookies
  const rejectAll = async () => {
    await savePreferences({
      ...DEFAULT_PREFERENCES,
      analytics_cookies: false,
      marketing_cookies: false,
      personalization_cookies: false,
      third_party_sharing: false,
    });
  };

  // Withdraw consent
  const withdrawConsent = async (consentType: keyof ConsentPreferences) => {
    if (!preferences) return;

    const newPreferences = {
      ...preferences,
      [consentType]: false,
    };

    await savePreferences(newPreferences);

    // Log withdrawal if logged in
    if (user) {
      await supabase.from("compliance_audit_log").insert({
        event_type: "consent_withdrawn",
        user_id: user.id,
        description: `User withdrew consent for ${consentType}`,
        metadata: { consent_type: consentType },
      });
    }
  };

  return {
    preferences,
    isLoading,
    hasConsented,
    isMinor,
    isCoppaAge,
    savePreferences,
    acceptAll,
    rejectAll,
    withdrawConsent,
    refetch: fetchPreferences,
  };
}
